import React from 'react';
import { Button } from '../components/ui/button';
import { Phone, Mail, Quote } from 'lucide-react';

const testimonials = [
  {
    company: 'Radiology Associates of Nevada',
    quote: "NPM's ability to translate IT into plain English impressed me. They have kept our best interests in mind, and they are a great group to work with.",
    name: 'Michael L. Young'
  },
  {
    company: 'Pueblo Medical Imaging',
    quote: "Our service staff has always been quick to respond, whether the issue is critical or routine. We use NPM for hardware, printing, and network responsibilities and are pleased with the results.",
    name: 'David LeVardi'
  },
  {
    company: 'R and T Services',
    quote: "Outstanding service at a fair market price. For Xerox supplies or repairs, it is the best service we have found.",
    name: 'Teresa Fitzgerald'
  },
  {
    company: 'Keizer Design',
    quote: "After my hard drive crashed, I thought that I would lose a client. I called David at NPM, and he had me up and running in no time, with no data lost.",
    name: 'Jeff Keizer'
  }
];

const TestimonialsPage = () => {
  return (
    <div className="relative overflow-hidden" data-testid="testimonials-page">
      {/* Hero Section */}
      <section className="relative hero-gradient section-padding-hero" data-testid="testimonials-hero">
        <div className="abstract-shape abstract-shape-1"></div>
        
        <div className="site-container relative">
          <div className="max-w-3xl prose-width">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#3f3f3f] leading-[1.15] mb-5">
              Testimonials
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed mb-4">
              Clients rely on NPM for responsive support, clear communication, and consistent results across devices, networks, and security.
            </p>
            <p className="text-base font-medium text-[#7dc242]">
              Serving Southern Nevada since 1999. More than 8,500 customers supported.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding grid-pattern" data-testid="testimonials-grid-section">
        <div className="site-container">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
              Client Testimonials
            </h2>
            <p className="text-lg lg:text-xl text-gray-500 max-w-2xl mx-auto">
              What our clients say about working with NPM
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="npm-card p-6 lg:p-8"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#7dc242]/10 flex items-center justify-center flex-shrink-0">
                    <Quote className="w-5 h-5 text-[#7dc242]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3f3f3f]">{testimonial.company}</h3>
                  </div>
                </div>
                <blockquote className="text-gray-600 text-[0.9375rem] leading-relaxed mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <p className="text-sm font-medium text-[#3f3f3f]">
                  — {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding cta-section" data-testid="testimonials-cta">
        <div className="site-container text-center">
          <h2 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-[#3f3f3f] mb-3">
            Ready to Join Our Clients?
          </h2>
          <p className="text-lg lg:text-xl text-gray-500 mb-6 max-w-2xl mx-auto">
            Experience responsive support and consistent results with NPM.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              asChild 
              className="bg-[#7dc242] hover:bg-[#6ba83a] hover:shadow-lg hover:-translate-y-0.5 text-white border-0 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="testimonials-cta-phone"
            >
              <a href="tel:+17027695422">
                <Phone className="w-[18px] h-[18px] mr-2" />
                Call (702) 769-5422
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              asChild 
              className="border-2 border-gray-500 text-gray-600 hover:border-[#7dc242] hover:text-[#5a9e32] hover:bg-[#7dc242]/5 h-11 px-6 text-[0.9375rem] transition-all"
              data-testid="testimonials-cta-email"
            >
              <a href="mailto:david@applevar.com">
                <Mail className="w-[18px] h-[18px] mr-2" />
                david@applevar.com
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;
